/**
 * PatientHistorySidebar.tsx
 *
 * Left-side collapsible sidebar (after main nav) showing recently visited patients.
 * Pinned patients persist at the top. Clicking navigates to patient detail.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Pin,
  PinOff,
  X,
  Users,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  usePatientHistoryStore,
  type PatientHistoryEntry,
} from '@/store/patientHistoryStore';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { prefetchPatientSidebar } from '@/hooks/usePatientDetail';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

// ─── Hover prefetch ──────────────────────────────────────────────────────────

function usePrefetchPatientOnHover() {
  const queryClient = useQueryClient();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  return useCallback(
    (patientId: number) => {
      if (!patientId) return;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        prefetchPatientSidebar(queryClient, patientId);
      }, 80);
    },
    [queryClient],
  );
}

// ─── Collapsed state (icon-only rail) ────────────────────────────────────────

function CollapsedRail() {
  const { entries, toggleSidebar } = usePatientHistoryStore();
  const navigate = useNavigate();
  const prefetchOnHover = usePrefetchPatientOnHover();

  const pinned = entries.filter((e) => e.pinned);
  const recent = entries.filter((e) => !e.pinned);
  const remainingSlots = Math.max(0, 12 - pinned.length);
  const visibleRecent = recent.slice(0, remainingSlots);

  const renderAvatar = (entry: PatientHistoryEntry) => (
    <button
      key={entry.patientId}
      onClick={() => navigate(`/patients/${entry.patientId}`)}
      onMouseEnter={() => prefetchOnHover(entry.patientId)}
      onFocus={() => prefetchOnHover(entry.patientId)}
      className="shrink-0 hover:ring-2 hover:ring-sidebar-primary rounded-full transition-all"
      title={entry.name}
    >
      <PatientAvatar
        patientId={entry.patientId}
        photoUrl={entry.photoUrl}
        name={entry.name}
        size="sm"
      />
    </button>
  );

  return (
    // hidden below md: on phones the rail ate 56px of a ~390px viewport
    <div className="hidden md:flex h-full w-14 flex-col items-center border-r border-sidebar-border bg-sidebar text-sidebar-foreground relative">
      {/* Expand button */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-3 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
        title="Expand patient history"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>

      {/* Icon + count */}
      <div className="mt-5 flex flex-col items-center gap-1">
        <Users className="h-5 w-5 text-sidebar-foreground/70" />
        {entries.length > 0 && (
          <span className="text-[10px] font-medium text-sidebar-foreground/50">
            {entries.length}
          </span>
        )}
      </div>

      {/* Mini avatar stack: pinned first, then divider, then recent */}
      <div className="mt-3 flex flex-col items-center gap-2 overflow-y-auto flex-1 px-1 pb-2">
        {pinned.map(renderAvatar)}
        {pinned.length > 0 && visibleRecent.length > 0 && (
          <div className="my-1 h-px w-8 bg-sidebar-foreground/25" aria-hidden="true" />
        )}
        {visibleRecent.map(renderAvatar)}
      </div>
    </div>
  );
}

// ─── Expanded state ──────────────────────────────────────────────────────────

function ExpandedPanel() {
  const { entries, togglePin, remove, clearHistory, toggleSidebar } =
    usePatientHistoryStore();
  const navigate = useNavigate();
  const location = useLocation();
  const prefetchOnHover = usePrefetchPatientOnHover();
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const pinned = entries.filter((e) => e.pinned);
  const recent = entries.filter((e) => !e.pinned);

  const activePatientId = (() => {
    const match = location.pathname.match(/^\/patients\/(\d+)/);
    return match ? Number(match[1]) : null;
  })();

  return (
    // hidden below md, matching the collapsed variant
    <div className="hidden md:flex h-full w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground relative">
      {/* Collapse button */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-3 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
        title="Collapse patient history"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>

      {/* Header */}
      <div className="flex items-center gap-1.5 px-3 pt-4 pb-2">
        <Users className="h-4 w-4 text-sidebar-foreground/70" />
        <span className="text-xs font-semibold text-sidebar-foreground/90 uppercase tracking-wider">
          Pinned
        </span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-1.5 pb-2">
        {entries.length === 0 && (
          <p className="px-2 py-6 text-center text-xs text-sidebar-foreground/40">
            Visited patients will appear here
          </p>
        )}

        {/* Pinned section */}
        {pinned.length > 0 && (
          <div className="mb-1">
            {pinned.map((entry) => (
              <PatientRow
                key={entry.patientId}
                entry={entry}
                isActive={entry.patientId === activePatientId}
                onNavigate={() => navigate(`/patients/${entry.patientId}`)}
                onHover={() => prefetchOnHover(entry.patientId)}
                onTogglePin={() => togglePin(entry.patientId)}
                onRemove={() => remove(entry.patientId)}
              />
            ))}
          </div>
        )}

        {/* Recent section */}
        {recent.length > 0 && (
          <div>
            {pinned.length > 0 && (
              <div className="mx-2 my-1 border-t border-sidebar-foreground/25" />
            )}
            <div className="flex items-center justify-between px-2 py-1">
              <p className="text-[10px] font-medium text-sidebar-foreground/40 uppercase tracking-wider">
                Recent
              </p>
              <button
                onClick={() => setConfirmClearOpen(true)}
                className="text-sidebar-foreground/40 hover:text-sidebar-foreground/70 transition-colors"
                title="Clear recent patients"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {recent.map((entry) => (
              <PatientRow
                key={entry.patientId}
                entry={entry}
                isActive={entry.patientId === activePatientId}
                onNavigate={() => navigate(`/patients/${entry.patientId}`)}
                onHover={() => prefetchOnHover(entry.patientId)}
                onTogglePin={() => togglePin(entry.patientId)}
                onRemove={() => remove(entry.patientId)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirm={clearHistory}
        title="Clear recent patients?"
        message="This will remove all non-pinned patients from your recents list. Pinned patients won't be affected."
        confirmLabel="Clear"
        variant="danger"
      />
    </div>
  );
}

// ─── Patient row ──────────────────────────────────────────────────────────────

function PatientRow({
  entry,
  isActive,
  onNavigate,
  onHover,
  onTogglePin,
  onRemove,
}: {
  entry: PatientHistoryEntry;
  isActive: boolean;
  onNavigate: () => void;
  onHover?: () => void;
  onTogglePin: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      onMouseEnter={onHover}
      className={cn(
        'group flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors',
        isActive
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'hover:bg-sidebar-accent/50 text-sidebar-foreground/80',
      )}
    >
      {/* Avatar + name */}
      <button
        onClick={onNavigate}
        onFocus={onHover}
        className="flex items-center gap-2 min-w-0 flex-1"
      >
        <PatientAvatar
          patientId={entry.patientId}
          photoUrl={entry.photoUrl}
          name={entry.name}
          size="sm"
          className="shrink-0"
        />
        <span className="text-sm font-semibold truncate">{entry.name}</span>
      </button>

      {/* Actions (visible on hover) */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          className="p-0.5 rounded hover:bg-sidebar-accent transition-colors"
          title={entry.pinned ? 'Unpin' : 'Pin'}
        >
          {entry.pinned ? (
            <PinOff className="h-3 w-3 text-sidebar-primary" />
          ) : (
            <Pin className="h-3 w-3 text-sidebar-foreground/50" />
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-0.5 rounded hover:bg-sidebar-accent transition-colors"
          title="Remove"
        >
          <X className="h-3 w-3 text-sidebar-foreground/50" />
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Main export ──────────────────────────────────────────────────────────────

export function PatientHistorySidebar() {
  const sidebarOpen = usePatientHistoryStore((s) => s.sidebarOpen);

  return sidebarOpen ? <ExpandedPanel /> : <CollapsedRail />;
}
